/**
 * Top-level routing. Signed-out users see the sign-in flow; a signed-in user who must
 * change their password or enrol MFA sees only that step. Every route in ROUTES is
 * guarded by its permissions (from auth/me); the API enforces them again.
 */
import { ShieldOff } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { EmptyState, PageBody, Spinner } from '../components/ui';
import { BRAND } from '../config/brand';
import { useAuth } from '../lib/auth';
import { MfaSetup, SetPassword, SignIn } from './auth/SignIn';
import { AppShell } from './shell/AppShell';
import { EXTRA_ROUTES, ROUTES, type RouteDef } from './routes';

function Guard({ route }: { route: RouteDef }) {
  const { can } = useAuth();
  const Page = route.page;
  if (!can(...route.perm)) return <NoAccess />;
  return <Page />;
}

export function NoAccess() {
  return (
    <PageBody>
      <EmptyState
        icon={<ShieldOff aria-hidden />}
        title="This screen is not part of your role"
        body="Ask your organisation’s administrator if you need it. Your role and the cabinets shared with you decide what you can open."
        action={
          <Link to="/" className="inline-flex min-h-touch items-center rounded-tag bg-brand px-4 text-sm font-semibold text-brand-ink">
            Go to home
          </Link>
        }
      />
    </PageBody>
  );
}

function NotFound() {
  const { pathname } = useLocation();
  return (
    <PageBody>
      <EmptyState
        title="Page not found"
        body={
          <>
            Nothing lives at <span className="code">{pathname}</span>.
          </>
        }
        action={
          <Link to="/" className="inline-flex min-h-touch items-center rounded-tag bg-brand px-4 text-sm font-semibold text-brand-ink">
            Go to home
          </Link>
        }
      />
    </PageBody>
  );
}

// D4 public pages (no account): share-link recipient view and certified-copy check.
const SharePublicPage = lazy(() => import('./pages/public/SharePublicPage'));
const VerifyPage = lazy(() => import('./pages/public/VerifyPage'));
// e-Sign (ES2): public check of a signed + sealed document (QR on the certificate of completion).
const VerifySignPage = lazy(() => import('./pages/public/VerifySignPage'));
// C6: public check of a certificate of destruction (QR on the certificate: PUBLIC_BASE_URL/verify-destruction/<token>).
const VerifyDestructionPage = lazy(() => import('./pages/public/VerifyDestructionPage'));

export function App() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (/^\/(s|verify|verify-sign|verify-destruction)\//.test(location.pathname)) {
    return (
      <Suspense fallback={<Spinner className="min-h-dvh" />}>
        <Routes>
          <Route path="/s/:token" element={<SharePublicPage />} />
          <Route path="/verify/:token" element={<VerifyPage />} />
          <Route path="/verify-sign/:id" element={<VerifySignPage />} />
          <Route path="/verify-destruction/:token" element={<VerifyDestructionPage />} />
        </Routes>
      </Suspense>
    );
  }
  if (loading) return <Spinner label={`Opening ${BRAND.product}…`} className="min-h-dvh" />;
  if (!user) return <SignIn />;
  if (user.mustChangePassword) return <SetPassword />;
  if (user.mfaRequired && !user.mfaEnabled) return <MfaSetup />;
  return (
    <Suspense fallback={<Spinner className="min-h-dvh" />}>
      <Routes>
        <Route element={<AppShell />}>
          {ROUTES.map((r) => (
            <Route key={r.path} path={r.path} element={<Guard route={r} />} />
          ))}
          {EXTRA_ROUTES.map((r) => (
            <Route key={r.path} path={r.path} element={<r.page />} />
          ))}
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
